/**
 * fx_sprite_batch — MANY fx sprites, ONE draw (2026-09-20).
 *
 * WHY: a festival square strung ~100 lanterns and pennants as individual
 * `THREE.Sprite`s — one render object, one uniform buffer and one draw call
 * EACH (plus 27 more per firework burst), on a renderer whose city frames are
 * CPU-bound on exactly that per-object cost. three r185 draws a Sprite
 * INSTANCED when `sprite.count > 1` and `SpriteNodeMaterial` takes the
 * per-instance centre (`positionNode`) and size (`scaleNode`), so a whole
 * string is one quad drawn N times.
 *
 * THE LOOK IS THE OLD ONE: the colour math is the classic SpriteMaterial's
 * (`texture × vec4(colour, opacity)`, colour converted sRGB → linear by
 * `THREE.Color` like `material.color` was), same blending, same fog, same
 * size attenuation, `markMrtOverlay`.
 *
 * LAWS:
 *  - `count` is FIXED at the capacity (≥ 2). three folds `count > 1` into the
 *    render object's cache key, so a count that crosses 1 (a firework batch
 *    going 0 → 1 → 27 → 0) would rebuild the shader every time. Dead slots are
 *    ZERO-SIZED quads; an all-dead batch is `visible = false` (no draw).
 *  - Per-instance data rides NODE-OWNED InstancedBufferAttributes — never the
 *    Sprite geometry, which three shares process-wide.
 *  - Positions are in the PARENT's space (the stage group); the batch object
 *    sits at `anchor`, which is only its transparent-sort depth. Two batches
 *    with the SAME anchor draw in creation order (three's stable sort falls
 *    back to the object id) — create the pennants before the lantern glow.
 */
import * as THREE from 'three/webgpu';
import { instancedDynamicBufferAttribute, texture, vec2, vec4 } from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';

/** One sprite of a batch. Mutate x/y/z/size freely; the owner calls `batch.commit()` once per frame. */
export interface FxSprite { x: number; y: number; z: number; size: number; readonly r: number; readonly g: number; readonly b: number; readonly a: number }

const scratch = new THREE.Color();

export class FxSpriteBatch {
  readonly sprite: THREE.Sprite;
  readonly capacity: number;
  private readonly live: FxSprite[] = [];
  private readonly posSize: Float32Array;
  private readonly color: Float32Array;
  private readonly posAttr: THREE.InstancedBufferAttribute;
  private readonly colAttr: THREE.InstancedBufferAttribute;
  private written = 0;
  private dirtyColor = true;
  dropped = 0;

  constructor(map: THREE.Texture, capacity: number, additive: boolean, private readonly anchor = new THREE.Vector3()) {
    this.capacity = Math.max(2, capacity | 0);
    this.posSize = new Float32Array(this.capacity * 4); // zero size = a degenerate quad
    this.color = new Float32Array(this.capacity * 4);
    this.posAttr = new THREE.InstancedBufferAttribute(this.posSize, 4);
    this.colAttr = new THREE.InstancedBufferAttribute(this.color, 4);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr.setUsage(THREE.DynamicDrawUsage);
    const aPos = instancedDynamicBufferAttribute(this.posAttr, 'vec4');
    const aCol = instancedDynamicBufferAttribute(this.colAttr, 'vec4');
    const mat = new THREE.SpriteNodeMaterial({ transparent: true, depthWrite: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending });
    mat.positionNode = aPos.xyz;
    mat.scaleNode = vec2(aPos.w, aPos.w);
    mat.colorNode = texture(map).mul(vec4(aCol.xyz, aCol.w)); // = SpriteMaterial: map × (colour, opacity)
    mat.name = additive ? 'fx-sprite-batch:add' : 'fx-sprite-batch';
    markMrtOverlay(mat); // fx sprites over the world leave the G-buffer alone (AO)
    const sp = new THREE.Sprite(mat as unknown as THREE.SpriteMaterial);
    sp.count = this.capacity;
    sp.frustumCulled = false; // three culls a Sprite as ONE unit quad at its origin
    sp.position.copy(anchor);
    sp.visible = false;
    this.sprite = sp;
  }

  /** A new sprite (null when the batch is full — the caller draws one fewer spark, never throws). */
  add(colorHex: number, size: number, opacity = 1, x = 0, y = 0, z = 0): FxSprite | null {
    if (this.live.length >= this.capacity) { this.dropped++; return null; }
    scratch.setHex(colorHex); // sRGB hex → linear working space, as material.color did
    const s: FxSprite = { x, y, z, size, r: scratch.r, g: scratch.g, b: scratch.b, a: opacity };
    this.live.push(s);
    this.dirtyColor = true;
    return s;
  }

  remove(s: FxSprite): void {
    const i = this.live.indexOf(s);
    if (i < 0) return;
    const last = this.live.pop()!;
    if (i < this.live.length) this.live[i] = last;
    this.dirtyColor = true;
  }

  clear(): void { if (this.live.length) { this.live.length = 0; this.dirtyColor = true; } }

  get count(): number { return this.live.length; }

  /** Pack the live sprites into the instance buffers. Once per frame, after the owner moved them. */
  commit(): void {
    const n = this.live.length, p = this.posSize, ax = this.anchor.x, ay = this.anchor.y, az = this.anchor.z;
    for (let i = 0; i < n; i++) {
      const s = this.live[i], o = i * 4;
      p[o] = s.x - ax; p[o + 1] = s.y - ay; p[o + 2] = s.z - az; p[o + 3] = s.size;
    }
    for (let i = n; i < this.written; i++) p[i * 4 + 3] = 0; // slots freed since the last commit
    if (n > 0 || this.written > 0) this.posAttr.needsUpdate = true;
    if (this.dirtyColor) {
      const c = this.color;
      for (let i = 0; i < n; i++) { const s = this.live[i], o = i * 4; c[o] = s.r; c[o + 1] = s.g; c[o + 2] = s.b; c[o + 3] = s.a; }
      this.colAttr.needsUpdate = true;
      this.dirtyColor = false;
    }
    this.written = n;
    this.sprite.visible = n > 0;
  }
}
